import { useEffect, useState } from 'react';
import { AlertTriangle, Clock, CheckCircle, XCircle, User, Store, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('masterToken');
  return { Authorization: `Bearer ${token}` };
};

const STATUS_META = {
  open: { icon: AlertTriangle, label: 'Open', color: 'text-[#FCD34D]', bg: 'bg-[#F59E0B]/15', border: 'border-[#F59E0B]/30' },
  in_review: { icon: Clock, label: 'In Review', color: 'text-[#93C5FD]', bg: 'bg-[#3B82F6]/15', border: 'border-[#3B82F6]/30' },
  resolved: { icon: CheckCircle, label: 'Resolved', color: 'text-[#86EFAC]', bg: 'bg-[#10B981]/15', border: 'border-[#10B981]/30' },
  dismissed: { icon: XCircle, label: 'Dismissed', color: 'text-[#9CA3AF]', bg: 'bg-white/5', border: 'border-white/10' },
};

export default function MasterComplaints() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_review: 0, resolved: 0, dismissed: 0, total: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/complaints/master/list?status=${filter}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRows(data.complaints || []);
        setCounts(data.counts || counts);
      }
    } catch {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const update = async (newStatus) => {
    if (!active) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/complaints/master/${active.complaint_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status: newStatus, resolution_note: resolutionNote }),
      });
      if (res.ok) {
        toast.success(`Complaint ${newStatus}`);
        setActive(null);
        setResolutionNote('');
        load();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d?.detail || 'Failed to update');
      }
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const statusMeta = (s) => STATUS_META[s] || STATUS_META.open;

  return (
    <div className="space-y-8" data-testid="master-complaints-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne">User Complaints</h1>
          <p className="text-[#9CA3AF] text-sm mt-1">
            Customer-submitted reports about vendors. Review, resolve, or dismiss.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm text-[#A78BFA] border border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/10"
          data-testid="complaints-refresh-btn"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['open', 'in_review', 'resolved', 'dismissed'].map((s) => {
          const meta = statusMeta(s);
          const Icon = meta.icon;
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`p-4 rounded-2xl border backdrop-blur-xl text-left transition-all ${
                filter === s ? `${meta.bg} ${meta.border}` : 'bg-white/5 border-white/10 hover:border-white/20'
              }`}
              data-testid={`complaints-filter-${s}`}
            >
              <div className={`flex items-center gap-2 mb-2 ${meta.color}`}>
                <Icon size={16} />
                <span className="text-xs font-semibold uppercase tracking-wide">{meta.label}</span>
              </div>
              <div className="text-2xl font-bold text-white font-mono">{counts[s] ?? 0}</div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
            filter === 'all' ? 'border-[#8B5CF6]/50 bg-[#8B5CF6]/10 text-white' : 'border-white/10 text-[#9CA3AF] hover:text-white'
          }`}
          data-testid="filter-all"
        >
          All ({counts.total || 0})
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="p-12 rounded-2xl bg-white/5 border border-white/10 text-center" data-testid="complaints-empty">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3 opacity-70" />
          <p className="text-white font-semibold">All clear</p>
          <p className="text-[#9CA3AF] text-sm mt-1">No complaints match this filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => {
            const meta = statusMeta(c.status);
            const Icon = meta.icon;
            return (
              <button
                key={c.complaint_id}
                onClick={() => { setActive(c); setResolutionNote(c.resolution_note || ''); }}
                className="w-full text-left p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-[#8B5CF6]/40 hover:bg-white/[0.07] transition-all"
                data-testid={`complaint-${c.complaint_id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.border} ${meta.bg} ${meta.color}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                      <span className="text-[#64748B] text-xs">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <h3 className="text-white font-semibold truncate">{c.subject}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-[#9CA3AF]">
                      <span className="flex items-center gap-1"><User size={12} /> {c.reporter_name || 'Unknown'}</span>
                      <span className="flex items-center gap-1"><Store size={12} /> {c.vendor_name || c.vendor_id}</span>
                    </div>
                  </div>
                </div>
                <p className="text-[#C4B5FD] text-sm line-clamp-2">{c.details}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {active && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setActive(null)}
          data-testid="complaint-modal"
        >
          <div
            className="bg-[#1A1A3E] border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-syne text-lg font-bold text-white">{active.subject}</h2>
                <p className="text-[#9CA3AF] text-xs mt-1">
                  {new Date(active.created_at).toLocaleString()} · {active.reporter_name} → {active.vendor_name}
                </p>
              </div>
              <button onClick={() => setActive(null)} className="text-[#9CA3AF] hover:text-white">✕</button>
            </div>

            <div className="space-y-4 mb-5">
              <section>
                <div className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wide mb-1">What happened</div>
                <p className="text-white text-sm whitespace-pre-wrap bg-white/5 border border-white/10 rounded-xl p-4">{active.details}</p>
              </section>
              <section>
                <div className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wide mb-1">Desired outcome</div>
                <p className="text-[#C4B5FD] text-sm whitespace-pre-wrap bg-[#8B5CF6]/5 border border-[#8B5CF6]/20 rounded-xl p-4">{active.desired_outcome}</p>
              </section>
              {active.booking_id && (
                <section>
                  <div className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wide mb-1">Linked booking</div>
                  <p className="font-mono text-xs text-[#A78BFA]">{active.booking_id}</p>
                </section>
              )}
              <section>
                <div className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wide mb-1">Reporter</div>
                <p className="text-white text-sm">{active.reporter_name} &lt;{active.reporter_email || 'no email'}&gt;</p>
              </section>
              <section>
                <div className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wide mb-1">Internal resolution note</div>
                <textarea
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  placeholder="Document what you did (refund, vendor warned, etc.)"
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm min-h-[80px]"
                  data-testid="complaint-resolution-note"
                />
              </section>
            </div>

            <div className="flex flex-wrap gap-3 pt-3 border-t border-white/10">
              <button
                disabled={saving}
                onClick={() => update('in_review')}
                className="px-4 py-2 rounded-xl text-sm bg-[#3B82F6]/20 text-[#93C5FD] border border-[#3B82F6]/30 hover:bg-[#3B82F6]/30 disabled:opacity-50"
                data-testid="complaint-mark-in-review"
              >
                Mark In Review
              </button>
              <button
                disabled={saving}
                onClick={() => update('resolved')}
                className="px-4 py-2 rounded-xl text-sm bg-[#10B981]/20 text-[#86EFAC] border border-[#10B981]/30 hover:bg-[#10B981]/30 disabled:opacity-50"
                data-testid="complaint-resolve"
              >
                Resolve
              </button>
              <button
                disabled={saving}
                onClick={() => update('dismissed')}
                className="px-4 py-2 rounded-xl text-sm bg-white/5 text-[#9CA3AF] border border-white/10 hover:bg-white/10 disabled:opacity-50"
                data-testid="complaint-dismiss"
              >
                Dismiss
              </button>
              <button
                onClick={() => setActive(null)}
                className="ml-auto px-4 py-2 rounded-xl text-sm text-[#9CA3AF] hover:text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
